import { CommonModule } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { VarDirective } from '@backoffice-frontend/shared/directives/common';
import {
    TimeSeriesFacade,
} from '@backoffice-frontend/time-series-identifier/domain';
import type { DeltaType, TableTimeSeries, TimeSeriesInput, WidgetTypeConfig } from '@clean-code/models';
import { indicate } from '@clean-code/shared/common';
import { ConfirmService } from '@clean-code/shared/components/ui-confirm';
import { BaseWidgetTypeContentComponent } from '@clean-code/shared/components/ui-dashboard';
import { LoadingIndicatorComponent, ProgressBarComponent } from '@clean-code/shared/components/ui-progress-bar';
import { TooltipDirective } from '@clean-code/shared/components/ui-tooltip';
import { TrendIconComponent } from '@clean-code/shared/components/ui-trend-icon';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
    faEdit,
    faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { TableDetailsDialogComponent } from '../table-widget-details-dialog/table-widget-details-dialog.component';
import { TableWidgetDetailsEditDialogComponent } from '../table-widget-details-edit-dialog/table-widget-details-edit-dialog.component';

@Component({
  selector: 'app-table-widget',
  templateUrl: './table-widget.component.html',
  styleUrls: ['./table-widget.component.css'],
  imports: [
    CommonModule,
    ProgressBarComponent,
    LoadingIndicatorComponent,
    TranslocoModule,
    FontAwesomeModule,
    VarDirective,
    TrendIconComponent,
    TooltipDirective,
  ],
})
export class TableWidgetComponent extends BaseWidgetTypeContentComponent<WidgetTypeConfig> {
  public data$: Observable<TableTimeSeries[]>;
  public faTriangle = faExclamationTriangle;
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public deltaType: DeltaType;
  public editMode$: Observable<boolean>;
  public faEdit = faEdit;
  public hideClosingValueHeader = input(false);

  private confirmService = inject(ConfirmService);

  public constructor() {
    super();

    const timeSeriesFacade = inject(TimeSeriesFacade);

    this.data$ = this.configObservable.pipe(
      filter((config: WidgetTypeConfig) => !!config),
      tap(
        (config: WidgetTypeConfig) =>
          (this.deltaType = config.settings.deltaType),
      ),
      switchMap((config: WidgetTypeConfig) =>
        timeSeriesFacade
          .getTimeSeriesTableData$(
            config?.dataSources?.map((x) => x.settings.identifier),
            'DAY',
            true,
          )
          .pipe(indicate(this.isLoading$)),
      ),
    );
  }

  public openDetails(
    tableTimeSeries: TableTimeSeries,
    dataSource: TimeSeriesInput,
  ) {
    this.confirmService
      .open(
        '',
        '',
        TableDetailsDialogComponent,
        {
          tableTimeSeries,
          name: tableTimeSeries.longName,
          dataSource,
        },
        {
          minWidth: '90vw',
          minHeight: '90vh',
        },
      )
      .subscribe();
  }

  public openDetailsConfig(dataSource: TimeSeriesInput) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '750px';

    this.confirmService
      .open(
        '',
        '',
        TableWidgetDetailsEditDialogComponent,
        dataSource,
        dialogConfig,
      )
      .pipe(
        filter(
          (config: { value: TimeSeriesInput; action: string }) => !!config,
        ),
      )
      .subscribe();
  }
}
